import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson } from '../api'
import { pageStyles } from '../styles'
import type { ThemeTokens } from '../theme'

/**
 * Access view A: the person view — overhaul item 9, with item 10's agents band.
 *
 * This is the screen that answers "what can this colleague reach", and it is
 * the screen an operator offboards from. Its whole design is a refusal: the
 * MCP servers and the agents are NOT drawn as one list of ticks, because a
 * tick against a server and a tick against an agent are produced by different
 * machinery with different latencies and different failure modes.
 *
 *   LIVE     decided at the MCP server, per request, from SSM + a Graph
 *            membership check. Revocation bites in about six minutes.
 *            Governs the web app AND Teams.
 *   CACHED   decided inside LibreChat from Mongo ACL rows whose group roster
 *            is written at sign-in and never pruned. Wrong in both directions.
 *
 * So there are two bands, each labelled with its mechanism, and above them a
 * DISAGREEMENT band that fires when the two sources contradict each other for
 * this person. That band is the most valuable thing this page can show: a
 * merged grid would assert an agreement that does not exist, and an operator
 * who trusted it would offboard someone and believe it was done.
 *
 * "SHARED, NOT VISIBLE" carries the design. It says both that the ACL row
 * exists and that the person cannot use it.
 */

interface ServerAccess {
  server: string
  displayName: string
  sensitive: boolean
  verdict: 'allow' | 'deny' | 'unknown'
  via: string | null
  unknownReason: string | null
}

interface GroupMembership {
  id: string
  name: string
  inEntra: boolean
  inLibreChat: boolean | null
  cachedMemberCount: number | null
  /** `absent` (LibreChat has no document for this group) is NOT the same
   *  fact as `unreadable` (the cached plane could not be read), and the two
   *  must not render the same way. */
  cachedGroupState: 'present' | 'absent' | 'unreadable'
}

interface AgentAccess {
  agent: string
  agentId: string | null
  verdict: 'visible' | 'shared-not-visible' | 'owner'
  via: string
  permBits: number | null
}

interface Disagreement {
  group: string
  kind: 'entra-only' | 'librechat-only'
  effect: string
}

export interface PersonAccess {
  person: {
    id: string
    displayName: string
    email: string
    department: string | null
    jobTitle: string | null
    accountEnabled: boolean | null
  }
  groups: GroupMembership[]
  live: { servers: ServerAccess[]; membershipError: string | null; source: string }
  cached: {
    agents: AgentAccess[]
    error: string | null
    hasLibreChatAccount: boolean | null
    source: string
  }
  disagreements: Disagreement[]
  keys: { aliases: string[]; error: string | null }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function PersonView({ tokens, email, onClear }: { tokens: ThemeTokens; email: string; onClear: () => void }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [data, setData] = useState<PersonAccess | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiJson<PersonAccess>(`/api/access/person?email=${encodeURIComponent(email)}`))
    } catch (err) {
      setData(null)
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [email])

  useEffect(() => {
    void load()
  }, [load])

  const liveBadge = {
    ...styles.badge,
    background: tokens.brand,
    color: tokens.brandContrast,
  }
  const cachedBadge = {
    ...styles.badge,
    background: tokens.surfaceRaised,
    color: tokens.textSecondary,
    border: `1px solid ${tokens.border}`,
  }

  // Layout and table primitives for the body below (Maroun, 24 Sep 2026:
  // "hard to read"). Local rather than in styles.ts: nothing else in the
  // console draws this shape yet.
  const twoColumns: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(28rem, 1fr))',
    gap: '1.6rem 2rem',
    marginTop: '1.2rem',
    alignItems: 'start',
  }
  const stack: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '1.6rem', minWidth: 0 }
  const blockTitle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0 0 0.6rem',
    fontSize: '0.95rem',
    fontWeight: 700,
    color: tokens.textPrimary,
  }
  const facts: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: '0.55rem 1.2rem',
    margin: 0,
    padding: '0.9rem 1rem',
    border: `1px solid ${tokens.border}`,
    borderRadius: '0.6rem',
    background: tokens.surfaceRaised,
    fontSize: '0.88rem',
  }
  const factLabel: React.CSSProperties = {
    color: tokens.textSecondary,
    fontWeight: 600,
    fontSize: '0.8rem',
    alignSelf: 'center',
  }
  const factValue: React.CSSProperties = { margin: 0, color: tokens.textPrimary, minWidth: 0, overflowWrap: 'anywhere' }
  const table: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.86rem',
    border: `1px solid ${tokens.border}`,
  }
  const th: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.5rem 0.8rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.textSecondary,
    background: tokens.surfaceRaised,
    borderBottom: `1px solid ${tokens.border}`,
  }
  const td: React.CSSProperties = {
    padding: '0.6rem 0.8rem',
    borderBottom: `1px solid ${tokens.border}`,
    verticalAlign: 'top',
    color: tokens.textPrimary,
  }
  const toneColours = {
    good: { background: 'rgba(0, 168, 110, 0.12)', color: tokens.brandHover },
    bad: { background: 'rgba(179, 38, 30, 0.1)', color: tokens.danger },
    neutral: { background: tokens.surfaceRaised, color: tokens.textSecondary, border: `1px solid ${tokens.border}` },
  }
  const pill = (text: string, tone: keyof typeof toneColours) => (
    <span
      style={{
        ...styles.badge,
        textTransform: 'none',
        letterSpacing: 0,
        fontSize: '0.76rem',
        whiteSpace: 'nowrap',
        ...toneColours[tone],
      }}
    >
      {text}
    </span>
  )

  return (
    <section style={styles.section} aria-label={`Access for ${email}`}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>
          Access · {data?.person.displayName ?? email}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" style={styles.buttonGhost} onClick={() => void load()} disabled={loading}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button type="button" style={styles.buttonGhost} onClick={onClear}>
            Clear
          </button>
        </div>
      </div>

      {error ? <p style={styles.error}>{error}</p> : null}
      {loading && !data ? <p style={styles.hint}>Resolving both planes for {email}…</p> : null}

      {data ? (
        <>
          {/* ---------------------------- THE FINDING ---------------------- */}
          {/* Rendered FIRST and loudest. Everything below is inventory; this
              is the thing an operator would otherwise never learn. */}
          {data.disagreements.length > 0 ? (
            <div
              style={{ ...styles.callout, borderLeft: `3px solid ${tokens.danger}` }}
              role="alert"
            >
              <strong>
                {data.disagreements.length === 1
                  ? 'TWO SOURCES DISAGREE FOR THIS PERSON'
                  : `TWO SOURCES DISAGREE FOR THIS PERSON, IN ${data.disagreements.length} GROUPS`}
              </strong>
              {data.disagreements.map((d) => (
                <div key={`${d.group}-${d.kind}`} style={{ marginTop: '0.4rem' }}>
                  <code>{d.group}</code>{' '}
                  <span style={{ ...cachedBadge, marginLeft: '0.25rem' }}>
                    {d.kind === 'entra-only' ? 'Entra only' : 'LibreChat only'}
                  </span>
                  <div style={{ color: tokens.textSecondary }}>{d.effect}</div>
                </div>
              ))}
            </div>
          ) : data.cached.error === null && data.live.membershipError === null ? (
            <div style={styles.callout}>
              <strong>The two sources agree for this person.</strong>
              <div style={{ color: tokens.textSecondary }}>
                Live Entra membership and LibreChat’s cached roster match on every group below. That is a statement
                about right now: it can stop being true the moment a group changes, and only a re-check tells you.
              </div>
            </div>
          ) : (
            <div style={{ ...styles.callout, borderLeft: `3px solid ${tokens.danger}` }}>
              <strong>Agreement between the two sources is UNKNOWN, not clean.</strong>
              <div style={{ color: tokens.textSecondary }}>
                One of the two planes could not be read, so no comparison is possible. Read the per-plane errors below
                rather than treating this page as an all-clear.
              </div>
            </div>
          )}

          {/* Two columns, each a stack of labelled blocks (Maroun, 24 Sep 2026:
              "hard to read"). Who the person is and which groups they sit in
              on the left; what that reaches on the right. Every list is a
              table with a status pill per row, rather than a stack of boxes
              each repeating the same sentence. */}
          <div style={twoColumns}>
            <div style={stack}>
              {/* ------------------------------ PERSON ---------------------- */}
              <div>
                <h3 style={blockTitle}>Person</h3>
                <dl style={facts}>
                  <dt style={factLabel}>Name</dt>
                  <dd style={factValue}>
                    <strong>{data.person.displayName}</strong>
                  </dd>
                  <dt style={factLabel}>Email</dt>
                  <dd style={factValue}>{data.person.email}</dd>
                  {data.person.jobTitle ? (
                    <>
                      <dt style={factLabel}>Job title</dt>
                      <dd style={factValue}>{data.person.jobTitle}</dd>
                    </>
                  ) : null}
                  {data.person.department ? (
                    <>
                      <dt style={factLabel}>Department</dt>
                      <dd style={factValue}>{data.person.department}</dd>
                    </>
                  ) : null}
                  <dt style={factLabel}>Entra</dt>
                  <dd style={factValue}>
                    {data.person.accountEnabled === null ? (
                      pill('unknown', 'neutral')
                    ) : data.person.accountEnabled ? (
                      pill('enabled', 'good')
                    ) : (
                      <span style={{ color: tokens.danger, fontWeight: 600 }}>
                        DISABLED — this account still holds everything below
                      </span>
                    )}
                  </dd>
                  <dt style={factLabel}>LibreChat account</dt>
                  <dd style={factValue}>
                    {data.cached.hasLibreChatAccount === null ? (
                      <span style={{ color: tokens.textSecondary }}>unknown (cache unreadable)</span>
                    ) : data.cached.hasLibreChatAccount ? (
                      pill('present', 'good')
                    ) : (
                      <span style={{ color: tokens.danger, fontWeight: 600 }}>
                        NONE — has never signed in, so no cached roster can contain them
                      </span>
                    )}
                  </dd>
                  <dt style={factLabel}>Developer keys</dt>
                  <dd style={factValue}>
                    {data.keys.error ? (
                      <span style={{ color: tokens.danger }}>unreadable: {data.keys.error}</span>
                    ) : data.keys.aliases.length === 0 ? (
                      <span style={{ color: tokens.textSecondary }}>none</span>
                    ) : (
                      <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {data.keys.aliases.map((alias) => (
                          <code key={alias}>{alias}</code>
                        ))}
                      </span>
                    )}
                  </dd>
                </dl>
                <p style={{ ...styles.hint, margin: '0.5rem 0 0' }}>
                  The developer API plane enforces no MCP authorisation at all today. A key here is reach that neither
                  band on the right governs.
                </p>
              </div>

              {/* ------------------------------ GROUPS ---------------------- */}
              <div>
                <h3 style={blockTitle}>Groups</h3>
                {data.groups.length === 0 ? (
                  <p style={styles.hint}>No group appears in any grant or any agent share.</p>
                ) : (
                  <>
                    {/* Said ONCE: repeating "the cached plane could not be
                        read" on every row was most of what made this block
                        hard to read. */}
                    {data.groups.some((g) => g.cachedGroupState === 'unreadable') ? (
                      <p style={{ ...styles.hint, margin: '0 0 0.5rem' }}>
                        LibreChat: the cached plane could not be read, so its column shows unreadable rather than a
                        guess.
                      </p>
                    ) : null}
                    <table style={table} aria-label="Groups">
                      <thead>
                        <tr>
                          <th style={th}>Group</th>
                          <th style={th}>Entra</th>
                          <th style={th}>LibreChat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.groups.map((group) => (
                          <tr key={group.id}>
                            <td style={td}>
                              <strong>{group.name}</strong>
                            </td>
                            <td style={td}>
                              {group.inEntra ? pill('member', 'good') : pill('not a member', 'neutral')}
                            </td>
                            <td style={td}>
                              {group.cachedGroupState === 'unreadable' ? (
                                pill('unreadable', 'neutral')
                              ) : group.cachedGroupState === 'absent' ? (
                                <span style={{ ...styles.hint, display: 'block', maxWidth: '22rem' }}>
                                  no group document — nobody has signed in through this group, so no agent share can
                                  key on it
                                </span>
                              ) : group.inLibreChat ? (
                                // Red only when the sources DISAGREE: a
                                // cached membership Entra no longer has.
                                pill(`member · roster of ${group.cachedMemberCount ?? '?'}`, group.inEntra ? 'good' : 'bad')
                              ) : group.inEntra ? (
                                pill(`MISSING · roster of ${group.cachedMemberCount ?? '?'}`, 'bad')
                              ) : (
                                // Neither source has them: agreement, not a
                                // finding (red here on v0.57.0 was wrong).
                                pill(`not a member · roster of ${group.cachedMemberCount ?? '?'}`, 'neutral')
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>

            <div style={stack}>
              {/* -------------------------- WHAT THAT REACHES ---------------- */}
              <div>
                <h3 style={blockTitle}>
                  MCP servers <span style={liveBadge}>Live</span>
                </h3>
                {data.live.membershipError ? (
                  <p style={styles.error}>
                    Live membership could not be resolved, so every group-derived verdict below is UNKNOWN rather than
                    DENY: {data.live.membershipError}
                  </p>
                ) : null}
                <table style={table} aria-label="MCP servers">
                  <thead>
                    <tr>
                      <th style={th}>Server</th>
                      <th style={th}>Access</th>
                      <th style={th}>Via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.live.servers.map((server) => (
                      <tr key={server.server}>
                        <td style={td}>
                          <code>{server.server}</code>
                          {server.sensitive ? (
                            <span style={{ color: tokens.danger, fontSize: '0.78rem', marginLeft: '0.35rem' }}>
                              ∗ sensitive
                            </span>
                          ) : null}
                        </td>
                        <td style={td}>
                          {server.verdict === 'allow'
                            ? pill('ALLOW', 'good')
                            : server.verdict === 'deny'
                              ? pill('deny', 'neutral')
                              : pill('UNKNOWN', 'bad')}
                        </td>
                        <td style={{ ...td, color: tokens.textSecondary }}>
                          {server.via ?? '—'}
                          {server.unknownReason ? (
                            <div style={{ color: tokens.danger, marginTop: '0.2rem' }}>{server.unknownReason}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <h3 style={blockTitle}>
                  Agents <span style={cachedBadge}>Cached</span>
                </h3>
                {data.cached.error ? (
                  <p style={styles.error}>{data.cached.error}</p>
                ) : data.cached.agents.length === 0 ? (
                  <p style={styles.hint}>No agent is shared with this person, on any principal.</p>
                ) : (
                  <table style={table} aria-label="Agents">
                    <thead>
                      <tr>
                        <th style={th}>Agent</th>
                        <th style={th}>Visibility</th>
                        <th style={th}>Via</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cached.agents.map((agent) => (
                        <tr key={`${agent.agent}-${agent.via}`}>
                          <td style={td}>
                            {agent.agent}
                            {agent.verdict === 'shared-not-visible' ? (
                              <div
                                style={{ color: tokens.danger, fontSize: '0.8rem', marginTop: '0.25rem', maxWidth: '26rem' }}
                              >
                                The ACL row exists; LibreChat’s cached roster does not list them, so the agent will not
                                appear until they next sign in.
                              </div>
                            ) : null}
                          </td>
                          <td style={td}>
                            {agent.verdict === 'visible'
                              ? pill('VISIBLE', 'good')
                              : agent.verdict === 'owner'
                                ? pill('OWNER', 'good')
                                : pill('SHARED, NOT VISIBLE', 'bad')}
                          </td>
                          <td style={{ ...td, color: tokens.textSecondary }}>{agent.via}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ------------------------------ SURFACES ---------------------- */}
          {/* The same grant governs two surfaces and a third enforces
              nothing. An operator reading only the bands above would not
              know that. */}
          <h3 style={{ ...styles.fieldLabel, marginTop: '1.2rem' }}>Surfaces</h3>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                {['', 'web', 'Teams', 'developer API'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.3rem 0.8rem 0.3rem 0', color: tokens.textSecondary }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>MCP access</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>LIVE</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>LIVE</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0', color: tokens.danger }}>not enforced on this plane</td>
              </tr>
              <tr>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>Agents</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>CACHED</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>n/a</td>
                <td style={{ padding: '0.3rem 0.8rem 0.3rem 0' }}>n/a</td>
              </tr>
            </tbody>
          </table>

          {/* Every claim above names the read that produced it. */}
          <p style={{ ...styles.hint, marginTop: '1rem' }}>
            <strong>Sources.</strong> LIVE: {data.live.source} CACHED: {data.cached.source} Keys: LiteLLM{' '}
            <code>/key/list</code>.
          </p>
        </>
      ) : null}
    </section>
  )
}
